// @figma file=tbd node=tbd name="SettingsShell" set="tbd" verified=tbd

import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SettingsNavItem {
  slug: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
}

export interface SettingsShellProps {
  items: SettingsNavItem[];
  activeSection: string;
  onSectionChange: (slug: string) => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Mobile navigation variant.
   * - 'tabs' (default): horizontal scrolling pill tabs
   * - 'list': drill-down list → detail (iOS Settings style); desktop sidebar is unchanged
   */
  mobileVariant?: 'tabs' | 'list';
}

// ─── SettingsShell ────────────────────────────────────────────────────────────

const SettingsShell = React.forwardRef<HTMLDivElement, SettingsShellProps>(
  ({ items, activeSection, onSectionChange, children, className, mobileVariant = 'tabs' }, ref) => {
    const activeButtonRef = React.useRef<HTMLButtonElement>(null);
    const [mobileDrilled, setMobileDrilled] = React.useState(false);

    React.useEffect(() => {
      activeButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }, [activeSection]);

    const activeItem = items.find((i) => i.slug === activeSection);
    const contentVisible = mobileVariant !== 'list' || mobileDrilled;

    return (
      <div
        ref={ref}
        className={cn('mt-4 flex flex-col lg:-mt-6 lg:flex-1 lg:flex-col lg:min-h-0', className)}
      >
        {/* Mobile pill tabs — tabs mode only */}
        {mobileVariant === 'tabs' && (
          <div
            className="lg:hidden -mx-4 sm:-mx-12 mb-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent)',
            }}
          >
            <nav
              aria-label="Settings navigation"
              className="flex min-w-max gap-2 px-4 sm:px-12 pb-0.5"
            >
              {items.map((item) => {
                const isActive = item.slug === activeSection;
                return (
                  <button
                    key={item.slug}
                    ref={isActive ? activeButtonRef : undefined}
                    type="button"
                    onClick={() => onSectionChange(item.slug)}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'flex shrink-0 cursor-pointer items-center gap-2 rounded-full px-4 h-8 text-sm transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      isActive
                        ? 'bg-muted font-medium text-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <span className="size-4 shrink-0">{item.icon}</span>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Card — contains sidebar (desktop) + list or content (mobile) */}
        <div className="rounded-xl border bg-card lg:flex lg:flex-1 lg:min-w-0 lg:overflow-hidden lg:min-h-0">
          {/* Desktop sidebar */}
          <nav
            aria-label="Settings navigation"
            className="hidden lg:flex w-52 shrink-0 flex-col gap-0.5 border-r px-3 pt-4 pb-3 overflow-y-auto"
          >
            {items.map((item) => {
              const isActive = item.slug === activeSection;
              return (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => onSectionChange(item.slug)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-left w-full transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )}
                >
                  <span className="size-4 shrink-0">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Mobile list — list mode, not drilled */}
          {mobileVariant === 'list' && !mobileDrilled && (
            <nav
              aria-label="Settings navigation"
              className="lg:hidden w-full divide-y divide-border"
            >
              {items.map((item) => (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => {
                    onSectionChange(item.slug);
                    setMobileDrilled(true);
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50 active:bg-muted"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    {item.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight">{item.label}</p>
                    {item.description && (
                      <p className="truncate text-xs text-muted-foreground">{item.description}</p>
                    )}
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </nav>
          )}

          {/* Content area — always on desktop; on mobile only when drilled */}
          <div
            className={cn(
              'flex-1 min-w-0 p-4 lg:pt-4 lg:px-6 lg:pb-6 lg:overflow-y-auto',
              !contentVisible && 'hidden lg:block',
            )}
          >
            {/* Back button — mobile, list mode, drilled */}
            {mobileVariant === 'list' && mobileDrilled && (
              <button
                type="button"
                onClick={() => setMobileDrilled(false)}
                className="lg:hidden flex items-center gap-1.5 -mx-4 -mt-4 mb-4 w-[calc(100%+2rem)] px-4 py-3 border-b border-border text-sm text-muted-foreground hover:bg-muted/50 active:bg-muted transition-colors"
              >
                <ChevronLeft className="size-4 shrink-0" />
                <span className="font-medium text-foreground">{activeItem?.label}</span>
              </button>
            )}
            {children}
          </div>
        </div>
      </div>
    );
  },
);
SettingsShell.displayName = 'SettingsShell';

export { SettingsShell };
